import type { ModelBuilder } from '@/reducers/model-builder'
import type { ConstraintFilterCondition } from '@/types/api/constraints'

import { isValidISIN } from '@/utils/instrument'

import {
  AllowBlockRequireEnum,
  AllowEnum,
  toAllowBlockRequireFilterCondition,
  toIsAllowableFilterCondition,
} from './allow-map'
import { createRangeFilter, fieldsToOmit } from './helpers'
import { MunicipalStatePreference } from './municipal-state-preference'
import {
  SLIDER_DEFAULTS,
  UniverseApiField,
  type UniverseKeys,
} from './universe-tab'

type UniverseConstraintModelBuilderStoreValues = Pick<
  ModelBuilder,
  UniverseKeys
>

/**
 * This function creates the universe constraint filters for the model builder.
 * It is leveraged by both the maturity buckets endpoint and the model preview
 * endpoint.
 *
 * @param model - The model builder object.
 * @returns The universe constraint filters.
 */
export function createUniverseConstraintFilters(
  model?: ModelBuilder,
): Array<Record<string, ConstraintFilterCondition>> {
  if (!model) return []
  const universeConstraints = {
    assetClasses: model.assetClasses,
    municipalStatePreference: model.municipalStatePreference,
    municipalState: model.municipalState,
    couponRange: model.couponRange,
    issuesSizeRange: model.issuesSizeRange,
    liquidityScoreRange: model.liquidityScoreRange,
    couponType: model.couponType,
    minSpRange: model.minSpRange,
    maxSpRange: model.maxSpRange,
    minLowestRatingRange: model.minLowestRatingRange,
    maxLowestRatingRange: model.maxLowestRatingRange,
    minMoodysRange: model.minMoodysRange,
    maxMoodysRange: model.maxMoodysRange,
    excludeSectors: model.excludeSectors,
    ytwRange: model.ytwRange,
    priceRange: model.priceRange,
    excludeUseOfProceeds: model.excludeUseOfProceeds,
    excludeMunicipalStates: model.excludeMunicipalStates,
    municipalTaxableFederal: model.municipalTaxableFederal,
    amtApplicable: model.amtApplicable,
    privatePlacement: model.privatePlacement,
    regulationS: model.regulationS,
    treasurySubtype: model.treasurySubtype,
    excludeIssuers: model.excludeIssuers,
    durationRange: model.durationRange,
    belowDeMinimisThreshold: model.belowDeMinimisThreshold,
    includeTickers: model.includeTickers,
    excludeTickers: model.excludeTickers,
    includeInstruments: model.includeInstruments,
    includeCurrencies: model.includeCurrencies,
    includeCountryDomicile: model.includeCountryDomicile,
    excludeGicsSectorL4: model.excludeGicsSectorL4,
    excludeIceSectorL4: model.excludeIceSectorL4,
    excludeStructures: model.excludeStructures,
    perpetual: model.perpetual,
    sinkingFund: model.sinkingFund,
    callable: model.callable,
    maturityRange: model.maturityRange,
    nextCallRange: model.nextCallRange,
    workoutRange: model.workoutRange,
    includeFilterList: model.includeFilterList,
    excludeFilterList: model.excludeFilterList,
    fdicInsured: model.fdicInsured,
    vrdoIndicator: model.vrdoIndicator,
    // [IMPORTANT] When updating this object with new universe constraints,
    // make sure to update the filters below so that it is
    // used by the maturity buckets endpoint
  } satisfies UniverseConstraintModelBuilderStoreValues

  const filters: Array<Record<string, ConstraintFilterCondition>> = []
  if (universeConstraints.assetClasses.length > 0) {
    filters.push({
      type: {
        in: universeConstraints.assetClasses,
      },
    })
  }
  if (
    universeConstraints.municipalStatePreference &&
    universeConstraints.municipalState &&
    // Only add this as a universe constraint if the municipal state preference is state focused
    universeConstraints.municipalStatePreference ===
      MunicipalStatePreference.StateFocused
  ) {
    filters.push({
      municipal_state: {
        equal_to: universeConstraints.municipalState,
      },
    })
  }

  const toOmit = fieldsToOmit(universeConstraints.assetClasses)

  // Range filters
  const fieldsToCreateRangeFilters: Record<string, UniverseKeys> = {
    [UniverseApiField.Coupon]: 'couponRange',
    [UniverseApiField.IssueSize]: 'issuesSizeRange',
    [UniverseApiField.LiquidityInstitutionalAggregate]: 'liquidityScoreRange',
    [UniverseApiField.Duration]: 'durationRange',
  }
  for (const [field, value] of Object.entries(fieldsToCreateRangeFilters)) {
    if (toOmit.includes(value)) continue
    const filter = createRangeFilter(
      field,
      universeConstraints[value] as number[],
      SLIDER_DEFAULTS[value as keyof typeof SLIDER_DEFAULTS].defaultValue,
    )
    if (filter) filters.push(filter)
  }

  // Multi-select inclusion filters
  const fieldsToCreateMultiSelectInclusionFilters: Record<
    string,
    [UniverseKeys, boolean] // Bool is to "force empty if empty" (ie: nothing selected => empty out the universe)
  > = {
    [UniverseApiField.CouponType]: ['couponType', true],
    [UniverseApiField.TreasurySubtype]: ['treasurySubtype', true],
    [UniverseApiField.IncludeTickers]: ['includeTickers', false],
    [UniverseApiField.Currency]: ['includeCurrencies', true],
    [UniverseApiField.CountryDomicile]: ['includeCountryDomicile', false],
  }
  for (const [field, value] of Object.entries(
    fieldsToCreateMultiSelectInclusionFilters,
  )) {
    const constraintField = value[0]
    const numSelected = Array.isArray(universeConstraints[constraintField])
      ? universeConstraints[constraintField].length
      : 0
    if (toOmit.includes(constraintField)) continue
    if (numSelected > 0) {
      const values = universeConstraints[constraintField]
      filters.push({
        [field]: {
          in: Array.isArray(values) ? (values as string[]) : [values as string],
        },
      })
    }
  }

  // Multi-select exclusion filters
  const fieldsToCreateMultiSelectExclusionFilters: Record<
    string,
    UniverseKeys
  > = {
    [UniverseApiField.Sector]: 'excludeSectors',
    [UniverseApiField.Issuer]: 'excludeIssuers',
    [UniverseApiField.UseOfProceeds]: 'excludeUseOfProceeds',
    [UniverseApiField.MunicipalState]: 'excludeMunicipalStates',
    [UniverseApiField.GicsSectorL4]: 'excludeGicsSectorL4',
    [UniverseApiField.IceSectorL4]: 'excludeIceSectorL4',
    // [IMPORTANT] This is a special case because the API field is called "ticker" but we want to exclude tickers
    [UniverseApiField.IncludeTickers]: 'excludeTickers',
  }
  for (const [field, value] of Object.entries(
    fieldsToCreateMultiSelectExclusionFilters,
  )) {
    if (toOmit.includes(value)) continue
    if (
      Array.isArray(universeConstraints[value]) &&
      universeConstraints[value].length > 0
    ) {
      filters.push({
        [field]: {
          not_in: universeConstraints[value] as string[],
        },
      })
    }
  }

  const spRatingFilter: ConstraintFilterCondition = {}
  // S&P Rating
  if (universeConstraints.minSpRange && !toOmit.includes('minSpRange')) {
    // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
    spRatingFilter['greater_than_or_equal_to'] = universeConstraints.minSpRange
  }
  if (universeConstraints.maxSpRange && !toOmit.includes('maxSpRange')) {
    // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
    spRatingFilter['less_than_or_equal_to'] = universeConstraints.maxSpRange
  }

  if (Object.keys(spRatingFilter).length > 0) {
    filters.push({
      sp_rating: spRatingFilter,
    })
  }

  const lowestRatingFilter: ConstraintFilterCondition = {}
  // Lowest Rating
  if (
    universeConstraints.minLowestRatingRange &&
    !toOmit.includes('minLowestRatingRange')
  ) {
    // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
    lowestRatingFilter['greater_than_or_equal_to'] =
      universeConstraints.minLowestRatingRange
  }
  if (
    universeConstraints.maxLowestRatingRange &&
    !toOmit.includes('maxLowestRatingRange')
  ) {
    // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
    lowestRatingFilter['less_than_or_equal_to'] =
      universeConstraints.maxLowestRatingRange
  }
  if (Object.keys(lowestRatingFilter).length > 0) {
    filters.push({
      lowest_rating: lowestRatingFilter,
    })
  }

  const moodysRatingFilter: ConstraintFilterCondition = {}
  // Moody's Rating
  if (
    universeConstraints.minMoodysRange &&
    !toOmit.includes('minMoodysRange')
  ) {
    // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
    moodysRatingFilter['greater_than_or_equal_to'] =
      universeConstraints.minMoodysRange
  }
  if (
    universeConstraints.maxMoodysRange &&
    !toOmit.includes('maxMoodysRange')
  ) {
    // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
    moodysRatingFilter['less_than_or_equal_to'] =
      universeConstraints.maxMoodysRange
  }
  if (Object.keys(moodysRatingFilter).length > 0) {
    filters.push({
      moodys_rating: moodysRatingFilter,
    })
  }

  // List filters
  if (universeConstraints.includeFilterList?.list) {
    const field = universeConstraints.includeFilterList.list.field.toLowerCase()
    const listId = universeConstraints.includeFilterList.list.list_id
    const scope = universeConstraints.includeFilterList.scope

    if (scope) {
      // When scope is defined, create: (in_filter_list) OR (not in scope)
      filters.push({
        [field]: {
          or: [{ in_filter_list: listId }, { not: scope }],
        } as ConstraintFilterCondition,
      })
    } else {
      // When scope is undefined, use the existing filter
      filters.push({
        [field]: {
          in_filter_list: listId,
        },
      })
    }
  }
  if (universeConstraints.excludeFilterList?.list) {
    const field = universeConstraints.excludeFilterList.list.field.toLowerCase()
    const listId = universeConstraints.excludeFilterList.list.list_id
    const scope = universeConstraints.excludeFilterList.scope

    if (scope) {
      // When scope is defined, create: (not_in_filter_list) OR (not in scope)
      filters.push({
        [field]: {
          or: [{ not_in_filter_list: listId }, { not: scope }],
        } as ConstraintFilterCondition,
      })
    } else {
      // When scope is undefined, use the existing filter
      filters.push({
        [field]: {
          not_in_filter_list: listId,
        },
      })
    }
  }

  // Single-select filters
  const fieldsToCreateSingleSelectFilters: Record<string, UniverseKeys> = {
    [UniverseApiField.MunicipalTaxableFederal]: 'municipalTaxableFederal',
    [UniverseApiField.IsAmtApplicable]: 'amtApplicable',
    [UniverseApiField.PrivatePlacement]: 'privatePlacement',
    [UniverseApiField.RegS]: 'regulationS',
    [UniverseApiField.FdicInsured]: 'fdicInsured',
  }
  for (const [field, value] of Object.entries(
    fieldsToCreateSingleSelectFilters,
  )) {
    if (toOmit.includes(value)) continue
    const filter = toIsAllowableFilterCondition(
      universeConstraints[value]
        ? (universeConstraints[value] as [AllowEnum])
        : // If the value is undefined in the model, that's the same as allowing all
          [AllowEnum.Allow],
    )
    if (filter) {
      filters.push({
        [field]: filter,
      })
    } else if (field === UniverseApiField.FdicInsured) {
      filters.push({
        fdic_insured: { equal_to: true },
      })
    }
  }

  // VRDO indicator (allow/block/require)
  if (!toOmit.includes('vrdoIndicator')) {
    const vrdoValue = universeConstraints.vrdoIndicator ?? [
      AllowBlockRequireEnum.Allow,
    ]
    const vrdoFilter = toAllowBlockRequireFilterCondition(
      vrdoValue as [AllowBlockRequireEnum],
    )
    if (vrdoFilter) {
      filters.push({
        [UniverseApiField.VrdoIndicator]: vrdoFilter,
      })
    }
  }

  // Exclude structures filter logic
  if (Array.isArray(model?.excludeStructures)) {
    const excludePerpetual = model.excludeStructures.includes('perpetual')
    const excludeSinking = model.excludeStructures.includes('sinking')
    const excludeCallable = model.excludeStructures.includes('callable')
    const excludeBullet = model.excludeStructures.includes('bullet')

    if (excludePerpetual) {
      filters.push({
        perpetual: { not_equal_to: true },
      })
    }
    if (excludeSinking) {
      filters.push({
        sinking_fund: { not_equal_to: true },
      })
    }
    if (excludeCallable) {
      filters.push({
        callable: { not_equal_to: true },
      })
    }
    if (excludeBullet) {
      filters.push({
        bullet: { not_equal_to: true },
      })
    }
  }

  return filters
}

export function createBuyUniverseConstraintFilters(
  model?: ModelBuilder,
): Record<string, ConstraintFilterCondition> {
  if (!model) return {}
  const buyUniverseConstraints = {
    ytwRange: model.ytwRange,
    priceRange: model.priceRange,
    belowDeMinimisThreshold: model.belowDeMinimisThreshold,
    includeInstruments: model.includeInstruments,
    maturityRange: model.maturityRange,
    nextCallRange: model.nextCallRange,
    workoutRange: model.workoutRange,
  }

  const filtersObject: Record<string, ConstraintFilterCondition> = {}

  if (
    buyUniverseConstraints.includeInstruments &&
    buyUniverseConstraints.includeInstruments.length > 0
  ) {
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    if (isValidISIN(buyUniverseConstraints.includeInstruments[0]!)) {
      filtersObject.isin = {
        in: buyUniverseConstraints.includeInstruments,
      }
    } else {
      filtersObject.cusip = {
        in: buyUniverseConstraints.includeInstruments,
      }
    }
  }

  if (buyUniverseConstraints.ytwRange) {
    const filter = createRangeFilter(
      'order_ytw',
      buyUniverseConstraints.ytwRange,
      SLIDER_DEFAULTS.ytwRange.defaultValue,
    )
    if (filter) Object.assign(filtersObject, filter)
  }

  if (buyUniverseConstraints.priceRange) {
    const filter = createRangeFilter(
      'order_price',
      buyUniverseConstraints.priceRange,
      SLIDER_DEFAULTS.priceRange.defaultValue,
    )
    if (filter) Object.assign(filtersObject, filter)
  }

  if (buyUniverseConstraints.belowDeMinimisThreshold) {
    const filter = toIsAllowableFilterCondition(
      buyUniverseConstraints.belowDeMinimisThreshold as [AllowEnum],
    )
    if (filter) filtersObject.below_de_minimis_threshold = filter
  }

  // Add maturity, next call, and workout range filters
  if (buyUniverseConstraints.maturityRange) {
    const filter = createRangeFilter(
      UniverseApiField.MaturityInYears,
      buyUniverseConstraints.maturityRange,
      SLIDER_DEFAULTS.maturityRange.defaultValue,
    )
    if (filter) Object.assign(filtersObject, filter)
  }

  if (buyUniverseConstraints.nextCallRange) {
    const filter = createRangeFilter(
      UniverseApiField.NextCallInYears,
      buyUniverseConstraints.nextCallRange,
      SLIDER_DEFAULTS.nextCallRange.defaultValue,
    )
    if (filter) Object.assign(filtersObject, filter)
  }

  if (buyUniverseConstraints.workoutRange) {
    const filter = createRangeFilter(
      UniverseApiField.WorkoutInYears,
      buyUniverseConstraints.workoutRange,
      SLIDER_DEFAULTS.workoutRange.defaultValue,
    )
    if (filter) Object.assign(filtersObject, filter)
  }

  return filtersObject
}
